import React from "react";
import { Models } from "@hex-insights/app-modules";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	equal,
	filterUnique,
	FormattedText,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	NotificationContainer,
	NotificationRenderProps,
	RequiredKeys,
	Row,
	Rule,
	Section,
	StyleProps,
	TruncatedText,
	useHasBeenFalse,
} from "@hex-insights/core";
import { Form, FormState, TextAreaInput, TextField, TextFieldInputProps } from "@hex-insights/forms";
import { Icon } from "../../../../../Components/Icon";
import {
	graphTypeNames,
	MessageGroupCreateMutation,
	Student,
	StudentNameAndImageFragment,
	User,
	UserAndUserGroupListForMessagesQuery,
	UserGroup,
	UserNameFragment,
} from "../../../../../Utilities";
import { EmployeeFormat, StudentFormat, UserFormat, UserGroupFormat } from "../../../../Format";
import { MessageCustomForm } from "../../../../Forms";
import { MediaAttachmentFormValues, MessageFormValues, MessageGroupMutation } from "../../../../FormUtilities";
import { MediaAttachmentUtilities, MessageAvailabilityPeriodUtilities } from "../../../../Utilities";
import * as MediaAttachmentComponents from "../../../MediaAttachment";
import * as StudentComponents from "../../../Student";
import { MessageGroupIcon } from "../../Icon";
import { MessageGroupCreateContext } from "../Context";
import styles from "./styles.module.css";

type NewMessageFormValues = Pick<MessageFormValues.Create, "body" | "mediaAttachments">;

export type RecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs: User["id"][];
	onClickUserGroupRecipient: (id: UserGroup["id"]) => void;
	selectedUserGroupIDs: UserGroup["id"][];
	onClickTopicStudent: (id: Student["id"]) => void;
	selectedTopicStudentIDs?: Student["id"][];
	onNextClick: () => void;
} & Partial<ClassNameProps & StyleProps>;

export type NewMessageRecipientsModalProps = {
	userRecipientIDs: User["id"][];
	setUserRecipientIDs: React.Dispatch<React.SetStateAction<User["id"][]>>;
	userGroupRecipientIDs: UserGroup["id"][];
	setUserGroupRecipientIDs: React.Dispatch<React.SetStateAction<UserGroup["id"][]>>;
	topicStudentIDs?: Student["id"][];
	setTopicStudentIDs: React.Dispatch<React.SetStateAction<Student["id"][]>>;
	RecipientSections: (props: RecipientSectionsProps) => JSX.Element | null;
	onNextClick: () => void;
	onCancelClick: () => void;
	nextDisabled?: boolean;
} & Pick<ModalProps, "ifRef" | "onClose">;

export function NewMessageRecipientsModal({
	userRecipientIDs,
	setUserRecipientIDs,
	userGroupRecipientIDs,
	setUserGroupRecipientIDs,
	topicStudentIDs,
	setTopicStudentIDs,
	RecipientSections,
	onNextClick,
	onCancelClick,
	nextDisabled,
	ifRef,
	onClose,
}: NewMessageRecipientsModalProps) {
	const onClickUserRecipient = React.useCallback(
		(id: User["id"]) => {
			setUserRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserRecipientIDs],
	);

	const onClickUserGroupRecipient = React.useCallback(
		(id: UserGroup["id"]) => {
			setUserGroupRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserGroupRecipientIDs],
	);

	const onClickTopicStudent = React.useCallback(
		(id: Student["id"]) => {
			setTopicStudentIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setTopicStudentIDs],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<Modal.Body style={{ display: "flex", flexDirection: "column" }}>
				<Column justify="spaced-start" align="center" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
					<strong style={{ fontSize: "1.25rem" }}>Select who you'd like to message</strong>

					<RecipientSections
						onClickUserRecipient={onClickUserRecipient}
						selectedUserIDs={userRecipientIDs}
						onClickUserGroupRecipient={onClickUserGroupRecipient}
						selectedUserGroupIDs={userGroupRecipientIDs}
						onClickTopicStudent={onClickTopicStudent}
						selectedTopicStudentIDs={topicStudentIDs}
						onNextClick={onNextClick}
						style={{ flexGrow: 1, width: "100%" }}
					/>
				</Column>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Row justify="spaced-end" className={styles["modal__footer__row"]}>
					<Button variant="secondary" onClick={onCancelClick}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={onNextClick}
						disabled={
							nextDisabled ||
							(userRecipientIDs.length === 0 &&
								userGroupRecipientIDs.length === 0 &&
								(topicStudentIDs?.length ?? 0) === 0)
						}
					>
						Next
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

export type NewMessageStudentTopicModalProps = {
	selectedStudentIDs: Student["id"][];
	setSelectedStudentIDs: React.Dispatch<React.SetStateAction<Student["id"][]>>;
	availableTopicStudents: (Pick<Student, "id"> & StudentNameAndImageFragment)[];
	onNextClick: () => void;
	onBackClick: () => void;
} & Pick<ModalProps, "ifRef" | "onClose">;

export function NewMessageStudentTopicModal({
	selectedStudentIDs,
	setSelectedStudentIDs,
	availableTopicStudents,
	onNextClick,
	onBackClick,
	ifRef,
	onClose,
}: NewMessageStudentTopicModalProps) {
	const onClickNoStudentButton = React.useCallback(() => {
		setSelectedStudentIDs([]);
		onNextClick();
	}, [setSelectedStudentIDs, onNextClick]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} confirmOnClose className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Heading.H3 noMargin style={{ textAlign: "center" }}>
						Select the student(s) this regards
					</Heading.H3>

					<Column justify="spaced-start">
						<Row justify="spaced-center" withVerticalSpacing overflow="wrap">
							{availableTopicStudents.map((student) => (
								<StudentButton
									key={student.id}
									student={student}
									isSelected={selectedStudentIDs.includes(student.id)}
									setSelectedStudentIDs={setSelectedStudentIDs}
								/>
							))}
						</Row>
						<Row justify="center">
							<NoStudentButton onClick={onClickNoStudentButton} />
						</Row>
					</Column>
				</Column>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Row justify="spaced-end" className={styles["modal__footer__row"]}>
					<Button variant="secondary" onClick={onBackClick}>
						Back
					</Button>
					<Button variant="primary" onClick={onNextClick}>
						Next
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

export type NewMessageStudentTopicWithAdditionalRecipientsModalProps = {
	selectedStudentIDs: Student["id"][];
	setSelectedStudentIDs: React.Dispatch<React.SetStateAction<Student["id"][]>>;
	availableTopicStudents: (Pick<Student, "id"> & StudentNameAndImageFragment)[];
	userRecipientIDs: User["id"][];
	setUserRecipientIDs: React.Dispatch<React.SetStateAction<User["id"][]>>;
	userGroupRecipientIDs: UserGroup["id"][];
	setUserGroupRecipientIDs: React.Dispatch<React.SetStateAction<UserGroup["id"][]>>;
	RecipientSections: (props: Omit<RecipientSectionsProps, "onClickTopicStudent">) => JSX.Element | null;
	onNextClick: () => void;
	onBackClick?: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "confirmOnClose">;

export function NewMessageStudentTopicWithAdditionalRecipientsModal({
	selectedStudentIDs,
	setSelectedStudentIDs,
	availableTopicStudents,
	userRecipientIDs,
	setUserRecipientIDs,
	userGroupRecipientIDs,
	setUserGroupRecipientIDs,
	RecipientSections,
	onNextClick,
	onBackClick,
	ifRef,
	onClose,
	confirmOnClose = true,
}: NewMessageStudentTopicWithAdditionalRecipientsModalProps) {
	const onClickNoStudentButton = React.useCallback(() => {
		setSelectedStudentIDs([]);
		onNextClick();
	}, [setSelectedStudentIDs, onNextClick]);

	const onClickUserRecipient = React.useCallback(
		(id: User["id"]) => {
			setUserRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserRecipientIDs],
	);

	const onClickUserGroupRecipient = React.useCallback(
		(id: UserGroup["id"]) => {
			setUserGroupRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserGroupRecipientIDs],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} confirmOnClose={confirmOnClose} className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Heading.H3 noMargin style={{ textAlign: "center" }}>
						Select the student(s) this regards
					</Heading.H3>

					<Column justify="spaced-start">
						<Row justify="spaced-center" withVerticalSpacing overflow="wrap">
							{availableTopicStudents.map((student) => (
								<StudentButton
									key={student.id}
									student={student}
									isSelected={selectedStudentIDs.includes(student.id)}
									setSelectedStudentIDs={setSelectedStudentIDs}
								/>
							))}
						</Row>
						<Row justify="center">
							<NoStudentButton onClick={onClickNoStudentButton} />
						</Row>

						<If condition={selectedStudentIDs.length > 0}>
							<Rule thickness="2px" color="var(--neutral-colors--stroke)" style={{ margin: "1rem 0" }} />

							<Section>
								<Section.Header>
									<Heading.H3 noMargin style={{ textAlign: "center" }}>
										Additional Recipients
									</Heading.H3>
								</Section.Header>
								<Section.Body>
									<RecipientSections
										onClickUserRecipient={onClickUserRecipient}
										selectedUserIDs={userRecipientIDs}
										onClickUserGroupRecipient={onClickUserGroupRecipient}
										selectedUserGroupIDs={userGroupRecipientIDs}
										selectedTopicStudentIDs={selectedStudentIDs}
										onNextClick={onNextClick}
										style={{ width: "100%" }}
									/>
								</Section.Body>
							</Section>
						</If>
					</Column>
				</Column>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Row justify="spaced-end" className={styles["modal__footer__row"]}>
					{!!onBackClick && (
						<Button variant="secondary" onClick={onBackClick}>
							Back
						</Button>
					)}
					<Button
						variant="primary"
						onClick={onNextClick}
						disabled={userRecipientIDs.length === 0 && userGroupRecipientIDs.length === 0}
					>
						Next
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

type StudentButtonProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	isSelected?: boolean;
	setSelectedStudentIDs: React.Dispatch<React.SetStateAction<Student["id"][]>>;
};

function StudentButton({ student, isSelected, setSelectedStudentIDs }: StudentButtonProps) {
	const studentID = student.id;
	const onClick = React.useCallback(() => {
		if (isSelected) {
			setSelectedStudentIDs((prev) => prev.filter((e) => e !== studentID));
		} else {
			setSelectedStudentIDs((prev) => [...prev, studentID]);
		}
	}, [isSelected, setSelectedStudentIDs, studentID]);

	return (
		<Button
			onClick={onClick}
			className={makeClassName(styles["student-button"], isSelected ? styles["student-button--selected"] : "")}
		>
			<Column justify="spaced-start" align="center">
				<StudentComponents.StudentIcon student={student} size={150} />
				<TruncatedText className={styles["student-button__name"]}>{StudentFormat.name(student)}</TruncatedText>
			</Column>
		</Button>
	);
}

type NoStudentButtonProps = {
	onClick: () => void;
};

function NoStudentButton({ onClick }: NoStudentButtonProps) {
	return (
		<Button onClick={onClick} className={styles["student-button"]}>
			<Column justify="spaced-start" align="center">
				<TruncatedText className={styles["student-button__name"]}>This isn't about a specific student.</TruncatedText>
			</Column>
		</Button>
	);
}

export type NewMessageMessageModalProps = {
	formState: FormState<NewMessageFormValues>;
	userGroupRecipientIDs: UserGroup["id"][];
	onNextClick: () => void;
	onBackClick?: () => void;
} & Pick<NewMessageMessageModalBodyProps, "userRecipientIDs" | "setUserRecipientIDs" | "RecipientSections"> &
	Pick<ModalProps, "ifRef" | "onClose">;

export function NewMessageMessageModal({
	formState,
	userGroupRecipientIDs,
	userRecipientIDs,
	setUserRecipientIDs,
	RecipientSections,
	onNextClick,
	onBackClick,
	ifRef,
	onClose,
}: NewMessageMessageModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} confirmOnClose className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<NewMessageMessageModalBody
				formState={formState}
				userRecipientIDs={userRecipientIDs}
				setUserRecipientIDs={setUserRecipientIDs}
				RecipientSections={RecipientSections}
			/>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Form.ActionBar>
					{!!onBackClick && (
						<Button variant="secondary" onClick={onBackClick}>
							Back
						</Button>
					)}
					<Button
						variant="primary"
						onClick={onNextClick}
						disabled={
							formState.formValues.body === "" || (userRecipientIDs.length === 0 && userGroupRecipientIDs.length === 0)
						}
					>
						Next
					</Button>
				</Form.ActionBar>
			</Modal.Footer>
		</Modal>
	);
}

export type NewMessageSubmittableMessageModalProps = {
	formState: FormState<NewMessageFormValues>;
	userID: User["id"];
	userRecipientIDs: User["id"][];
	userGroupRecipientIDs: UserGroup["id"][];
	selectedStudentIDs: Student["id"][];
	body: string;
	mediaAttachments: MediaAttachmentFormValues.CreateInMessage[];
	onBackClick?: () => void;
	onSuccess: (data?: MessageGroupCreateMutation["createMessageGroup"] | null) => void;
} & Pick<NewMessageMessageModalBodyProps, "setUserRecipientIDs" | "RecipientSections"> &
	Pick<ModalProps, "ifRef" | "onClose">;

export function NewMessageSubmittableMessageModal({
	formState,
	userID,
	userRecipientIDs,
	userGroupRecipientIDs,
	selectedStudentIDs,
	setUserRecipientIDs,
	RecipientSections,
	onBackClick,
	onSuccess: onSuccessProp,
	ifRef,
	onClose,
}: NewMessageSubmittableMessageModalProps) {
	const { applyCreate } = useMessageSubmission({
		userID,
		userRecipientIDs,
		userGroupRecipientIDs,
		selectedStudentIDs,
		body: formState.formValues.body,
		mediaAttachments: formState.formValues.mediaAttachments,
	});

	const onSuccess = React.useCallback(
		(data?: MessageGroupCreateMutation["createMessageGroup"] | null) => {
			onSuccessProp(data);
			if (onClose) {
				onClose();
			}
		},
		[onSuccessProp, onClose],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} confirmOnClose className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<NewMessageMessageModalBody
				formState={formState}
				userRecipientIDs={userRecipientIDs}
				setUserRecipientIDs={setUserRecipientIDs}
				RecipientSections={RecipientSections}
			/>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Form.ActionBar>
					<Column justify="spaced-start">
						<Row justify="spaced-end">
							{!!onBackClick && (
								<Button variant="secondary" onClick={onBackClick}>
									Back
								</Button>
							)}
							<Models.ActionButton
								variant="primary"
								performAction={applyCreate}
								SuccessNotification={SendMessageSuccessNotification}
								onSuccess={onSuccess}
								disabled={
									formState.formValues.body === "" ||
									(userRecipientIDs.length === 0 && userGroupRecipientIDs.length === 0)
								}
							>
								Send Message
							</Models.ActionButton>
						</Row>
					</Column>
				</Form.ActionBar>
			</Modal.Footer>
		</Modal>
	);
}

export type MessageEditRecipientSectionsProps = Pick<
	RecipientSectionsProps,
	"selectedUserIDs" | "onClickUserRecipient"
>;

export type NewMessageMessageModalBodyProps = {
	formState: FormState<NewMessageFormValues>;
} & Pick<MessageModalRecipientsModalProps, "userRecipientIDs" | "setUserRecipientIDs" | "RecipientSections">;

export function NewMessageMessageModalBody({
	formState,
	userRecipientIDs,
	setUserRecipientIDs,
	RecipientSections,
}: NewMessageMessageModalBodyProps) {
	const {
		selectedUserRecipients,
		selectedUserGroupRecipients,
		selectedTopicStudents: selectedStudents,
	} = React.useContext(MessageGroupCreateContext);
	const hasSelectedStudents = selectedStudents?.length > 0;
	const recipientGridTracks = React.useMemo(
		() => ({
			0: 1,
			500: hasSelectedStudents ? 2 : 1,
		}),
		[hasSelectedStudents],
	);

	const { onAddMediaAttachment, onUpdateMediaAttachment, onRemoveMediaAttachment } =
		MessageCustomForm.useMediaAttachmentsState(formState);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const ref = React.useRef<HTMLDivElement | null>(null);

	return (
		<Modal.Body>
			<div ref={ref}>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Heading.H3 noMargin style={{ textAlign: "center" }}>
						Write your message
					</Heading.H3>

					<Column justify="spaced-start">
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							<Button variant="link" size="small" onClick={openModal}>
								Edit Recipients
							</Button>

							<Modal.If condition={isModalOpen}>
								<MessageModalRecipientsModal
									userRecipientIDs={userRecipientIDs}
									setUserRecipientIDs={setUserRecipientIDs}
									RecipientSections={RecipientSections}
									onClose={closeModal}
								/>
							</Modal.If>

							<Grid responsiveTracks={recipientGridTracks} gap="var(--general--spaced---horizontal-spacing)">
								<MessageRecipients
									userRecipients={selectedUserRecipients}
									userGroupRecipients={selectedUserGroupRecipients}
									variant="writeMessage"
								/>
								{hasSelectedStudents && <TopicStudents students={selectedStudents} variant="writeMessage" />}
							</Grid>
						</Column>

						<TextField
							formState={formState}
							name="body"
							autoFocus
							noHeader
							Input={MessageBodyInput}
							placeholder="Write your message here..."
							style={{ width: "100%" }}
						/>

						<MessageCustomForm.MediaAttachmentDropArea
							formState={formState}
							onAddMediaAttachment={onAddMediaAttachment}
							dragStartElementRef={ref}
							className={styles["media-attachment-drop-area"]}
						/>

						<Row justify="space-between" align="flex-start">
							<Conditional>
								<If condition={formState.formValues.mediaAttachments.length > 0}>
									<MessageCustomForm.MediaAttachmentsList
										mediaAttachmentFormValues={formState.formValues.mediaAttachments}
										onUpdateMediaAttachment={onUpdateMediaAttachment}
										onRemoveMediaAttachment={onRemoveMediaAttachment}
										style={{ padding: "0.25rem 0.5rem" }}
									/>
								</If>
								<Else>
									<span></span>
								</Else>
							</Conditional>

							<MessageCustomForm.MediaAttachmentAddButton
								onAdd={onAddMediaAttachment}
								numItems={formState.formValues.mediaAttachments.length}
							>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Paperclip size={18} /> Add Attachment
								</Row>
							</MessageCustomForm.MediaAttachmentAddButton>
						</Row>
					</Column>
				</Column>
			</div>
		</Modal.Body>
	);
}

type MessageModalRecipientsModalProps = {
	userRecipientIDs: User["id"][];
	setUserRecipientIDs: React.Dispatch<React.SetStateAction<User["id"][]>>;
	RecipientSections: (props: MessageEditRecipientSectionsProps) => JSX.Element | null;
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

function MessageModalRecipientsModal({
	userRecipientIDs,
	setUserRecipientIDs,
	RecipientSections,
	ifRef,
	onClose,
}: MessageModalRecipientsModalProps) {
	const [initialUserRecipientIDs] = React.useState(userRecipientIDs);
	const hasChangedUserRecipientIDs = useHasBeenFalse(equal(initialUserRecipientIDs, userRecipientIDs));

	const onClickUserRecipient = React.useCallback(
		(id: User["id"]) => {
			setUserRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserRecipientIDs],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} style={{ height: "50rem" }}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Recipients</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<RecipientSections selectedUserIDs={userRecipientIDs} onClickUserRecipient={onClickUserRecipient} />
			</Modal.Body>
			<If condition={hasChangedUserRecipientIDs}>
				<Modal.Footer>
					<Row justify="flex-end">
						<Button variant="primary" onClick={onClose}>
							Confirm Selection
						</Button>
					</Row>
				</Modal.Footer>
			</If>
		</Modal>
	);
}

function MessageBodyInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={12} />;
}

export type NewMessageReviewModalProps = {
	userID: User["id"];
	userRecipientIDs: User["id"][];
	userGroupRecipientIDs: UserGroup["id"][];
	selectedStudentIDs: Student["id"][];
	body: string;
	mediaAttachments: MediaAttachmentFormValues.CreateInMessage[];
	onBackClick: () => void;
	onSuccess: (data?: MessageGroupCreateMutation["createMessageGroup"] | null) => void;
} & Pick<ModalProps, "ifRef" | "onClose">;

export function NewMessageReviewModal({
	userID,
	userRecipientIDs,
	userGroupRecipientIDs,
	selectedStudentIDs,
	body,
	mediaAttachments,
	onBackClick,
	ifRef,
	onClose,
	onSuccess: onSuccessProp,
}: NewMessageReviewModalProps) {
	const {
		selectedUserRecipients,
		selectedUserGroupRecipients,
		selectedTopicStudents: selectedStudents,
	} = React.useContext(MessageGroupCreateContext);
	const isSingleUserRecipient = selectedUserRecipients.length === 1 && selectedUserGroupRecipients.length === 0;
	const singleUserRecipient = selectedUserRecipients[0] as
		| UserAndUserGroupListForMessagesQuery["userConnection"]["edges"][0]["node"]
		| undefined;
	const singleUserRecipientMessageAvailabilityPeriods = singleUserRecipient?.messageAvailabilityPeriods ?? [];
	const hasSelectedStudents = selectedStudents?.length > 0;
	const recipientGridTracks = React.useMemo(
		() => ({
			0: 1,
			500: hasSelectedStudents ? 2 : 1,
		}),
		[hasSelectedStudents],
	);

	const { applyCreate } = useMessageSubmission({
		userID,
		userRecipientIDs,
		userGroupRecipientIDs,
		selectedStudentIDs,
		body,
		mediaAttachments,
	});

	const onSuccess = React.useCallback(
		(data?: MessageGroupCreateMutation["createMessageGroup"] | null) => {
			onSuccessProp(data);
			if (onClose) {
				onClose();
			}
		},
		[onSuccessProp, onClose],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} confirmOnClose className={styles["new-message-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Start a New Chat</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Heading.H3 noMargin style={{ textAlign: "center" }}>
						Review and send
					</Heading.H3>

					<If
						condition={
							isSingleUserRecipient &&
							!singleUserRecipient?.isCurrent &&
							singleUserRecipientMessageAvailabilityPeriods.length > 0 &&
							!MessageAvailabilityPeriodUtilities.isUserAvailable(singleUserRecipientMessageAvailabilityPeriods)
						}
					>
						{!!singleUserRecipient && (
							<Row
								justify="spaced-start"
								align="center"
								horizontalSpacing="0.625rem"
								className={styles["new-message-modal--review-modal__user-unavailable-alert"]}
							>
								<Icon.WarningOctagon color="var(--theme-colors__pink)" />
								{UserFormat.name(singleUserRecipient)} is currently unavailable to chat. You can send a message, but
								they will not receive it until their available hours.
							</Row>
						)}
					</If>

					<Column justify="spaced-start" verticalSpacing="1.5rem">
						<Grid responsiveTracks={recipientGridTracks} gap="var(--general--spaced---horizontal-spacing)">
							<Column justify="spaced-start">
								<span className={styles["new-message-modal--review-modal__header__title"]}>Send Message To:</span>
								<MessageRecipients
									userRecipients={selectedUserRecipients}
									userGroupRecipients={selectedUserGroupRecipients}
									variant="review"
								/>
							</Column>

							{hasSelectedStudents && (
								<Column justify="spaced-start">
									<span className={styles["new-message-modal--review-modal__header__title"]}>Regarding:</span>
									<TopicStudents students={selectedStudents} variant="review" />
								</Column>
							)}
						</Grid>

						<Column justify="spaced-start">
							<span className={styles["new-message-modal--review-modal__header__title"]}>Message:</span>
							<FormattedText className={styles["new-message-modal--message-body-preview"]}>{body}</FormattedText>
						</Column>

						<Row justify="spaced-start" withVerticalSpacing overflow="wrap">
							{mediaAttachments.map((mediaAttachment, i) => {
								if (mediaAttachment.isFile && MediaAttachmentUtilities.isVisual(mediaAttachment)) {
									return (
										<MediaAttachmentComponents.VisualAttachmentPreview
											key={i}
											formValues={mediaAttachment}
											className={styles["media-attachment-preview"]}
										/>
									);
								}
								if (mediaAttachment.isFile) {
									return (
										<MediaAttachmentComponents.FileAttachmentPreview
											key={i}
											formValues={mediaAttachment}
											className={styles["media-attachment-preview"]}
										/>
									);
								}

								return (
									<MediaAttachmentComponents.LinkAttachmentPreview
										key={i}
										formValues={mediaAttachment}
										className={styles["media-attachment-preview"]}
									/>
								);
							})}
						</Row>
					</Column>
				</Column>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer--pass-through"]}>
				<Row justify="spaced-end" className={styles["modal__footer__row"]}>
					<Button variant="secondary" onClick={onBackClick}>
						Edit
					</Button>
					<Models.ActionButton
						variant="primary"
						performAction={applyCreate}
						SuccessNotification={SendMessageSuccessNotification}
						onSuccess={onSuccess}
					>
						Send Message
					</Models.ActionButton>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

type UseMessageSubmissionArgs = {
	userID: User["id"];
	userRecipientIDs: User["id"][];
	userGroupRecipientIDs: UserGroup["id"][];
	selectedStudentIDs: Student["id"][];
	body: string;
	mediaAttachments: MediaAttachmentFormValues.CreateInMessage[];
};

function useMessageSubmission({
	userID,
	userRecipientIDs,
	userGroupRecipientIDs,
	selectedStudentIDs,
	body,
	mediaAttachments,
}: UseMessageSubmissionArgs) {
	const { selectedUserGroupRecipients } = React.useContext(MessageGroupCreateContext);

	const isUserMemberOfGroup = selectedUserGroupRecipients.some((userGroup) =>
		userGroup.members.some((user) => user.id === userID),
	);

	const create = MessageGroupMutation.useCreateOrUpdate();
	const applyCreate = React.useCallback(async () => {
		return create({
			customName: "",
			isOneOnOne: userRecipientIDs.length === 1 && userGroupRecipientIDs.length === 0,
			userIDs: isUserMemberOfGroup ? filterUnique(userRecipientIDs) : filterUnique([userID, ...userRecipientIDs]),
			userGroupIDs: userGroupRecipientIDs,
			messages: [
				{
					body,
					messageTopic: [
						{
							name: "",
							studentIDs: selectedStudentIDs,
						},
					],
					mediaAttachments,
				},
			],
		});
	}, [
		create,
		userRecipientIDs,
		userGroupRecipientIDs,
		isUserMemberOfGroup,
		userID,
		body,
		mediaAttachments,
		selectedStudentIDs,
	]);

	return { applyCreate };
}

function SendMessageSuccessNotification(props: NotificationRenderProps) {
	return (
		<NotificationContainer variant="success" {...props}>
			Message sent!
		</NotificationContainer>
	);
}

type MessageRecipientProps = {
	userRecipients: UserAndUserGroupListForMessagesQuery["userConnection"]["edges"][0]["node"][];
	userGroupRecipients: UserAndUserGroupListForMessagesQuery["userGroupConnection"]["edges"][0]["node"][];
	variant: "writeMessage" | "review";
};

function MessageRecipients({ userRecipients, userGroupRecipients, variant }: MessageRecipientProps) {
	const isSingleUserRecipient = userRecipients.length === 1 && userGroupRecipients.length === 0;
	const singleUserRecipient = userRecipients[0] as
		| UserAndUserGroupListForMessagesQuery["userConnection"]["edges"][0]["node"]
		| undefined;
	const singleUserRecipientDescription = EmployeeFormat.Fields.jobTitle(
		singleUserRecipient?.person?.employee?.jobTitle ?? "",
	);

	// only used if single recipient
	const { todaysAvailability, hasAvailabilitySet } = React.useMemo(() => {
		const singleUserRecipientMessageAvailabilityPeriods = singleUserRecipient?.messageAvailabilityPeriods ?? [];
		const todaysAvailability = MessageAvailabilityPeriodUtilities.getUserTodayAvailability(
			singleUserRecipientMessageAvailabilityPeriods,
		);
		return { todaysAvailability, hasAvailabilitySet: singleUserRecipientMessageAvailabilityPeriods.length > 0 };
	}, [singleUserRecipient]);

	const toNames = englishCommaJoinStrings(
		[...userGroupRecipients, ...userRecipients].map((e) =>
			e.__typename === graphTypeNames.UserGroup ? UserGroupFormat.name(e) : UserFormat.name(e as UserNameFragment),
		),
	);

	if (variant === "writeMessage") {
		return (
			<Row justify="spaced-start" className={styles["new-message-modal--message-modal__header"]}>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1, overflow: "hidden" }}>
					<Conditional>
						<If condition={isSingleUserRecipient}>
							{!!singleUserRecipient && (
								<React.Fragment>
									<TruncatedText className={styles["new-message-modal--message-modal__header__title"]}>
										To: {UserFormat.name(singleUserRecipient)}
									</TruncatedText>
									<If condition={singleUserRecipientDescription !== ""}>
										<TruncatedText className={styles["new-message-modal--message-modal__header__detail"]}>
											{singleUserRecipientDescription}
										</TruncatedText>
									</If>
									<If condition={hasAvailabilitySet}>
										<TruncatedText
											className={
												styles["new-message-modal--message-modal__header__detail"] +
												" " +
												styles["new-message-modal--availability"]
											}
										>
											{todaysAvailability}
										</TruncatedText>
									</If>
								</React.Fragment>
							)}
						</If>
						<Else>
							<TruncatedText lines={3} className={styles["new-message-modal--message-modal__header__title"]}>
								To: {toNames}
							</TruncatedText>
						</Else>
					</Conditional>
				</Column>

				<MessageGroupIcon messageGroup={{ users: userRecipients, userGroups: userGroupRecipients }} size={60} />
			</Row>
		);
	}

	if (variant === "review") {
		return (
			<Row justify="spaced-start" align="center">
				<MessageGroupIcon messageGroup={{ users: userRecipients, userGroups: userGroupRecipients }} size={60} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ overflow: "hidden" }}>
					<Conditional>
						<If condition={isSingleUserRecipient}>
							{!!singleUserRecipient && (
								<React.Fragment>
									<TruncatedText className={styles["new-message-modal--review-modal__header__title"]}>
										{UserFormat.name(singleUserRecipient)}
									</TruncatedText>
									<If condition={singleUserRecipientDescription !== ""}>
										<TruncatedText className={styles["new-message-modal--review-modal__header__detail"]}>
											{singleUserRecipientDescription}
										</TruncatedText>
									</If>
									<If condition={hasAvailabilitySet}>
										<TruncatedText
											className={
												styles["new-message-modal--review-modal__header__detail"] +
												" " +
												styles["new-message-modal--availability"]
											}
										>
											{todaysAvailability}
										</TruncatedText>
									</If>
								</React.Fragment>
							)}
						</If>
						<Else>
							<TruncatedText lines={3} className={styles["new-message-modal--review-modal__header__title"]}>
								{toNames}
							</TruncatedText>
						</Else>
					</Conditional>
				</Column>
			</Row>
		);
	}

	return null;
}

type TopicStudentsProps = {
	students: (Pick<Student, "id"> & StudentNameAndImageFragment)[];
	variant: "writeMessage" | "review";
};

function TopicStudents({ students, variant }: TopicStudentsProps) {
	if (variant === "writeMessage") {
		return (
			<Row justify="spaced-start" className={styles["new-message-modal--message-modal__header"]}>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1, overflow: "hidden" }}>
					<TruncatedText lines={3} className={styles["new-message-modal--message-modal__header__title"]}>
						Re: {englishCommaJoinStrings(students.map((e) => StudentFormat.name(e)))}
					</TruncatedText>
				</Column>

				<StudentComponents.StudentMultiIcon students={students} size={60} />
			</Row>
		);
	}

	if (variant === "review") {
		return (
			<Row justify="spaced-start">
				<StudentComponents.StudentMultiIcon students={students} size={60} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ overflow: "hidden" }}>
					<TruncatedText lines={3} className={styles["new-message-modal--review-modal__header__title"]}>
						{englishCommaJoinStrings(students.map((e) => StudentFormat.name(e)))}
					</TruncatedText>
				</Column>
			</Row>
		);
	}

	return null;
}

export type NewMessageLoadingModalProps = Pick<ModalProps, "ifRef" | "onClose"> & Partial<ChildrenProps>;

export function NewMessageLoadingModal({ ifRef, onClose, children }: NewMessageLoadingModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={styles["new-message-modal"]}>
			<Modal.Body style={{ display: "flex", flexDirection: "column" }}>
				<Column justify="center" align="center" style={{ flexGrow: 1 }}>
					{children}
				</Column>
			</Modal.Body>
		</Modal>
	);
}
